import React, { useState, useEffect } from 'react';
import mhApi from '../services/marketing-hub';
import { texteTheme } from '../utils/theme';
import './MarketingHub.css'; // réutilise les classes de formulaire du Hub (mh-form-grid, mh-cat-chip…)

/**
 * Partagé par les pages Google Analytics / Google Ads / Search Console :
 * - useSiteActions : charge les actions du site rattaché à la propriété affichée
 *   (résolution via le registre mkt_sites) pour la période courante.
 * - actionMarkers : associe chaque action à la date de courbe la plus proche
 *   (axe X catégoriel au format YYYYMMDD) → repères <ReferenceLine>.
 * - QuickActionModal : saisie rapide d'une action depuis la page (site pré-résolu).
 */

const CAT = {
  refonte:     { label: 'Refonte',     emoji: '🏗️', color: 'var(--color-accent)' },
  contenu_seo: { label: 'Contenu SEO', emoji: '✍️', color: 'var(--color-success)' },
  campagne:    { label: 'Campagne',    emoji: '📣', color: 'var(--color-info)' },
  technique:   { label: 'Technique',   emoji: '🔧', color: 'var(--color-warning)' },
  tracking:    { label: 'Tracking',    emoji: '🎯', color: 'var(--color-decision)' },
  netlinking:  { label: 'Netlinking',  emoji: '🔗', color: 'var(--text-secondary)' },
  autre:       { label: 'Autre',       emoji: '📌', color: 'var(--text-muted)' },
};
export const ACTION_CAT_META = CAT;

const toKey = (d) => String(d).slice(0, 10).replace(/-/g, ''); // 'YYYY-MM-DD' → 'YYYYMMDD'

// Charge { actions, siteId } pour un canal + identifiant + période. `enabled=false`
// (ex. non super-admin ou canal non désiré) court-circuite l'appel.
export function useSiteActions(channel, key, start, end, enabled = true, refreshToken = 0) {
  const [data, setData] = useState({ actions: [], siteId: null, siteName: null });
  useEffect(() => {
    if (!enabled || !channel || !key || !start || !end) { setData({ actions: [], siteId: null, siteName: null }); return; }
    let alive = true;
    mhApi.chartActions({ channel, key, start, end })
      .then(r => { if (alive) setData({ actions: r.data?.actions || [], siteId: r.data?.site_id || null, siteName: r.data?.site_name || null }); })
      .catch(() => { if (alive) setData({ actions: [], siteId: null, siteName: null }); });
    return () => { alive = false; };
  }, [channel, key, start, end, enabled, refreshToken]);
  return data;
}

// Associe chaque action à la date de courbe la plus proche (axe catégoriel).
export function actionMarkers(actions, chartDates) {
  if (!actions?.length || !chartDates?.length) return [];
  return actions.map(a => {
    const target = Number(toKey(a.action_date));
    let best = chartDates[0], bestDiff = Infinity;
    for (const d of chartDates) {
      const diff = Math.abs(Number(d) - target);
      if (diff < bestDiff) { bestDiff = diff; best = d; }
    }
    const cat = CAT[a.category] || CAT.autre;
    return { key: a.id, x: best, emoji: cat.emoji, color: cat.color, title: a.title, date: String(a.action_date).slice(0, 10), catLabel: cat.label };
  });
}

// Regroupe les repères par date de courbe (pour l'infobulle du graphe).
export function groupMarkersByDate(markers) {
  const map = {};
  (markers || []).forEach(m => { (map[m.x] = map[m.x] || []).push(m); });
  return map;
}

// Lignes « action » à injecter dans l'infobulle d'un graphe (au survol d'une date).
export function ActionTooltipRows({ items }) {
  if (!items || !items.length) return null;
  return (
    <div className="sa-tt-actions">
      {items.map(a => (
        <div key={a.key} className="sa-tt-action">
          <span className="sa-tt-emoji" style={{ color: texteTheme(a.color) }}>{a.emoji}</span>
          <span><b>{a.catLabel}</b> — {a.title}</span>
        </div>
      ))}
    </div>
  );
}

// Petite modale de saisie rapide depuis une page analytics (site déjà résolu).
export function QuickActionModal({ siteId, siteName, defaultChannel, onClose, onSaved }) {
  const [form, setForm] = useState({
    action_date: new Date().toISOString().slice(0, 10),
    category: 'refonte',
    channels: [defaultChannel || 'tous'],
    title: '',
    expected_impact: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm(f => ({ ...f, ...patch }));
  const canSave = siteId && form.action_date && form.title.trim();
  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try { await mhApi.addSiteAction(siteId, form); onSaved && onSaved(); }
    catch (e) { alert('Erreur : ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(false); }
  };
  return (
    <div className="modal-overlay">
      <div className="modal-content small" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>🗓️ Nouvelle action</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          {!siteId ? (
            <p className="mh-empty-link">
              Cette propriété n'est rattachée à aucun site du <strong>Hub Supervision</strong>.
              Renseignez son identifiant (GA4 / Ads / Search Console) sur la fiche du site dans le Hub pour pouvoir y consigner des actions.
            </p>
          ) : (
            <>
              <p className="mh-quick-site">Site : <strong>{siteName}</strong></p>
              <div className="mh-form-grid">
                <label className="mh-col2">Date *
                  <input type="date" value={form.action_date} onChange={e => set({ action_date: e.target.value })} />
                </label>

                <div className="mh-form-section">Catégorie</div>
                <div className="mh-cat-grid">
                  {Object.entries(CAT).map(([k, v]) => (
                    <button key={k} type="button" className={`mh-cat-chip${form.category === k ? ' active' : ''}`}
                      style={{ '--_c': v.color }} onClick={() => set({ category: k })}>
                      <span className="mh-cat-emoji">{v.emoji}</span>{v.label}
                    </button>
                  ))}
                </div>

                <label className="mh-col2">Titre *
                  <input type="text" placeholder="ex. Refonte de la page d'accueil" value={form.title} onChange={e => set({ title: e.target.value })} />
                </label>
                <label className="mh-col2">Impact attendu
                  <input type="text" placeholder="ex. hausse trafic organique" value={form.expected_impact} onChange={e => set({ expected_impact: e.target.value })} />
                </label>
                <p className="mh-quick-note">Visible sur cette courbe. Pour cibler d'autres canaux, utilisez le Hub → Sites → Journal d'actions.</p>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Fermer</button>
          {siteId && <button className="btn-primary" onClick={save} disabled={!canSave || saving}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>}
        </div>
      </div>
    </div>
  );
}
